import React, { useState, useEffect } from "react";
import {
  IconTrash,
  IconPlus,
  IconX,
  IconRefresh,
  IconCheck,
  IconLogout,
  IconExternalLink
} from "@tabler/icons-react";
import api, { API_URL, formatFileUrl } from "../../utils/api";

const API_BASE = `${API_URL}/gallery`;

export const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  
  const [isModalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ 
    title: "", 
    description: "", 
    headerDesc: "",
    imageUrl: "", 
    gridImages: [],
    bottomTitle: "",
    bottomDesc1: "",
    bottomDesc2: "",
    category: "General" 
  });
  const [editId, setEditId] = useState(null);

  const showNotification = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    window.location.href = `/${process.env.REACT_APP_ADMIN_ROUTE || 'admin-library'}/login`;
  };

  const fetchItems = async () => {
    setIsRefreshing(true);
    try {
      const res = await api.get(API_BASE);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      showNotification("Failed to fetch gallery items", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleUpload = async (e, field = "imageUrl") => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      showNotification("Uploading...", "info");
      const res = await api.post(`${API_BASE}/upload`, formData);
      if (field === "gridImages") {
        setForm((prev) => ({ ...prev, gridImages: [...prev.gridImages, res.data.url] }));
      } else {
        setForm((prev) => ({ ...prev, [field]: res.data.url }));
      }
      showNotification("Image uploaded successfully");
    } catch (err) {
      console.error(err);
      showNotification("Upload failed", "error");
    }
  };

  const removeGridImage = (index) => {
    setForm(prev => {
      const newGrid = [...prev.gridImages];
      newGrid.splice(index, 1);
      return { ...prev, gridImages: newGrid };
    });
  };

  const saveItem = async (e) => {
    e.preventDefault();
    if (!form.title || !form.imageUrl) {
      return showNotification("Title and Main Image are required", "error");
    }
    setIsSaving(true);
    try {
      if (editId) {
        await api.put(`${API_BASE}/${editId}`, form);
        showNotification("Gallery item updated");
      } else {
        await api.post(API_BASE, form);
        showNotification("Gallery item created");
      }
      setModalOpen(false);
      setEditId(null);
      fetchItems();
    } catch (err) {
      console.error(err);
      showNotification("Failed to save gallery item", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Are you sure you want to delete this gallery item?")) return;
    try {
      await api.delete(`${API_BASE}/${id}`);
      fetchItems();
      showNotification("Item deleted");
    } catch (err) {
      console.error(err);
      showNotification("Error deleting item", "error");
    }
  };

  const openNew = () => {
    setForm({ 
      title: "", 
      description: "", 
      headerDesc: "",
      imageUrl: "", 
      gridImages: [],
      bottomTitle: "",
      bottomDesc1: "",
      bottomDesc2: "",
      category: "General" 
    });
    setEditId(null);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setForm({ 
      title: item.title || "", 
      description: item.description || "", 
      headerDesc: item.headerDesc || "",
      imageUrl: item.imageUrl || "", 
      gridImages: item.gridImages || [],
      bottomTitle: item.bottomTitle || "",
      bottomDesc1: item.bottomDesc1 || "",
      bottomDesc2: item.bottomDesc2 || "",
      category: item.category || "General" 
    });
    setEditId(item._id);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] pt-8 pb-12 px-4 md:px-8 font-sans">
      {notification && (
        <div 
          className="fixed top-8 left-1/2 -translate-x-1/2 z-[200] px-6 py-3 rounded-2xl shadow-2xl flex items-center space-x-3 transition-all"
          style={{ 
            backgroundColor: notification.type === "success" ? "#000000" : notification.type === "error" ? "#dc2626" : "#2563eb",
            color: "#ffffff"
          }}
        >
          <IconCheck size={18} color="#ffffff" />
          <span className="text-sm font-medium">{notification.msg}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="px-2 py-0.5 bg-pumpkinToast-50 text-pumpkinToast-600 text-[10px] font-bold uppercase rounded-md tracking-wider border border-pumpkinToast-100">Admin Dashboard</span>
            <h1 className="text-2xl sm:text-3xl font-medium text-neutral-900 tracking-tight mt-2">Gallery <span className="text-neutral-400">Management</span></h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={handleLogout} className="flex items-center space-x-2 px-6 py-3 bg-red-50 text-red-600 rounded-2xl font-medium border border-red-100 hover:bg-red-100 transition-all shadow-sm">
              <span className="text-sm">Logout</span>
              <IconLogout size={16} />
            </button>
            <a href="/gallery" target="_blank" rel="noreferrer" className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-2xl font-medium border border-neutral-200 hover:bg-neutral-800 transition-all shadow-sm">
              <span className="text-sm">Visit Gallery</span>
              <IconExternalLink size={16} className="text-neutral-400" />
            </a>
            <button onClick={fetchItems} disabled={isRefreshing} className={`p-3 rounded-2xl bg-white border border-neutral-200 text-neutral-500 transition-all shadow-sm ${isRefreshing ? "opacity-50" : ""}`}>
              <IconRefresh size={20} className={isRefreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Tabbed Navigation to go back */}
        <div className="flex overflow-x-auto items-center gap-2 mb-8 bg-neutral-100/60 p-1.5 rounded-[22px] w-fit max-w-full scrollbar-hide border border-neutral-200/50">
          {[
            { id: "all", label: "Dashboard", href: `/${process.env.REACT_APP_ADMIN_ROUTE || 'admin-library'}` },
            { id: "gallery", label: "Gallery", href: "#" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.href !== "#") window.location.href = tab.href;
              }}
              className={`px-6 py-2.5 rounded-2xl font-medium transition-all flex items-center space-x-2 text-sm whitespace-nowrap ${
                tab.id === "gallery"
                  ? "bg-white text-neutral-900 shadow-md ring-1 ring-black/5"
                  : "text-neutral-500 hover:text-neutral-800 hover:bg-white/50"
              }`}
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200/80 shadow-sm overflow-hidden p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-medium text-lg">Gallery Items</h3>
            <button onClick={openNew} className="flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors shadow-md">
              <IconPlus size={18} />
              <span>Add New</span>
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {items.map(item => (
              <div key={item._id} className="group relative bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all">
                <div className="aspect-[4/3] bg-neutral-100 relative overflow-hidden cursor-pointer" onClick={() => openEdit(item)}>
                  <img src={formatFileUrl(item.imageUrl)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <h4 className="font-medium text-neutral-900 truncate">{item.title}</h4>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteItem(item._id); }} className="absolute top-3 right-3 p-2 bg-red-50 text-red-600 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100">
                  <IconTrash size={16} />
                </button>
              </div>
            ))}
          </div>
          {items.length === 0 && (
            <div className="text-center py-12 text-neutral-400">No gallery items found.</div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setModalOpen(false)}></div>
          <div className="bg-white rounded-[2rem] w-full max-w-6xl shadow-2xl relative z-10 flex flex-col max-h-[95vh] overflow-hidden">
            <div className="px-8 py-5 border-b border-neutral-100 flex items-center justify-between shrink-0 bg-white">
              <h3 className="text-xl font-semibold text-neutral-900">{editId ? "Edit Gallery Item" : "Create New Gallery Item"}</h3>
              <button onClick={() => setModalOpen(false)} className="p-2 text-neutral-400 hover:text-red-500 bg-neutral-50 hover:bg-red-50 rounded-full transition-all"><IconX size={20} /></button>
            </div>
            
            <div className="p-8 overflow-y-auto custom-scrollbar bg-neutral-50/30">
              <form id="gallery-form" onSubmit={saveItem}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  
                  {/* Column 1: Main Content */}
                  <div className="space-y-5 bg-white p-6 rounded-2xl border border-neutral-200/60 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm">1</div>
                      <h4 className="font-semibold text-neutral-800">Main Content</h4>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-2">Headline (Title) *</label>
                      <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-neutral-900" placeholder="e.g. Masjid inauguration" required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-2">Main Paragraph</label>
                      <textarea value={form.headerDesc} onChange={e => setForm({...form, headerDesc: e.target.value})} rows={4} className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-neutral-900 resize-none leading-relaxed" placeholder="Detailed description for the top section..." />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-2">Card Excerpt (Optional)</label>
                      <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-neutral-900 resize-none leading-relaxed" placeholder="Short text for the gallery list card..." />
                    </div>
                  </div>

                  {/* Column 2: Additional Details */}
                  <div className="space-y-5 bg-white p-6 rounded-2xl border border-neutral-200/60 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-sm">2</div>
                      <h4 className="font-semibold text-neutral-800">Additional Details</h4>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-2">Sub Headline</label>
                      <input type="text" value={form.bottomTitle} onChange={e => setForm({...form, bottomTitle: e.target.value})} className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all text-neutral-900" placeholder="e.g. Event Highlights" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-2">Paragraph 1</label>
                      <textarea value={form.bottomDesc1} onChange={e => setForm({...form, bottomDesc1: e.target.value})} rows={3} className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all text-neutral-900 resize-none leading-relaxed" placeholder="First paragraph for the bottom section..." />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-2">Paragraph 2</label>
                      <textarea value={form.bottomDesc2} onChange={e => setForm({...form, bottomDesc2: e.target.value})} rows={3} className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-xl text-sm focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all text-neutral-900 resize-none leading-relaxed" placeholder="Second paragraph for the bottom section..." />
                    </div>
                  </div>

                  {/* Column 3: Media Uploads */}
                  <div className="space-y-5 bg-white p-6 rounded-2xl border border-neutral-200/60 shadow-sm flex flex-col">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-sm">3</div>
                      <h4 className="font-semibold text-neutral-800">Media Gallery</h4>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider mb-2">Main Image (Required) *</label>
                      {form.imageUrl ? (
                        <div className="relative group w-full h-32 rounded-xl overflow-hidden border-2 border-purple-100 shadow-sm">
                          <img src={formatFileUrl(form.imageUrl)} alt="Main" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[2px]">
                            <button type="button" onClick={() => setForm({...form, imageUrl: ""})} className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transform hover:scale-110 transition-all shadow-lg"><IconTrash size={16} /></button>
                          </div>
                        </div>
                      ) : (
                        <div className="relative group w-full h-32">
                          <input type="file" onChange={(e) => handleUpload(e, "imageUrl")} accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                          <div className="w-full h-full border-2 border-dashed border-neutral-300 rounded-xl bg-neutral-50 flex flex-col items-center justify-center text-neutral-500 group-hover:bg-purple-50 group-hover:border-purple-300 group-hover:text-purple-600 transition-all">
                            <IconPlus size={24} className="mb-1 opacity-50" />
                            <span className="text-sm font-medium">Upload Main Image</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 mt-4">
                      <div className="flex justify-between items-end mb-2">
                        <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wider">Grid Images</label>
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-neutral-100 text-neutral-500 rounded-md">{form.gridImages.length} / 4</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {form.gridImages.map((imgUrl, idx) => (
                          <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-neutral-200 shadow-sm">
                            <img src={formatFileUrl(imgUrl)} alt={`Grid ${idx}`} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[2px]">
                              <button type="button" onClick={() => removeGridImage(idx)} className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transform hover:scale-110 transition-all shadow-lg"><IconTrash size={14} /></button>
                            </div>
                          </div>
                        ))}
                        
                        {form.gridImages.length < 4 && (
                          <div className="relative group aspect-square">
                            <input type="file" onChange={(e) => handleUpload(e, "gridImages")} accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                            <div className="w-full h-full border-2 border-dashed border-neutral-300 rounded-xl bg-neutral-50 flex flex-col items-center justify-center text-neutral-500 group-hover:bg-purple-50 group-hover:border-purple-300 group-hover:text-purple-600 transition-all">
                              <IconPlus size={20} className="mb-1 opacity-50" />
                              <span className="text-xs font-medium">Add Grid</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </form>
            </div>

            <div className="px-8 py-5 border-t border-neutral-100 bg-white flex justify-between items-center shrink-0 rounded-b-[2rem]">
              <p className="text-xs text-neutral-400 font-medium">Fields marked with * are required.</p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setModalOpen(false)} className="px-6 py-2.5 text-neutral-600 font-semibold hover:bg-neutral-100 rounded-xl transition-colors">Cancel</button>
                <button type="submit" form="gallery-form" disabled={isSaving} className="px-8 py-2.5 bg-black text-white font-semibold rounded-xl hover:bg-neutral-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:hover:translate-y-0 flex items-center gap-2">
                  {isSaving ? <IconRefresh size={18} className="animate-spin" /> : <IconCheck size={18} />}
                  <span>{isSaving ? "Saving..." : "Save Gallery Item"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
